import { addIntegration, setTag } from "@sentry/nuxt";
import { getReplay } from "@sentry/vue";

const useSentryReplays = (options: {
  enabled: MaybeRefOrGetter<boolean>;
  name?: string;
  autoStartReplay?: MaybeRefOrGetter<boolean>;
}) => {
  const $config = useRuntimeConfig();

  const replaysLoaded = ref(false);
  const shouldAutoStartReplay = computed(
    () => toValue(options.autoStartReplay) && replaysLoaded.value,
  );

  watchEffect(async () => {
    // Only load replays for PROD
    if (!$config.public.isProdEnvironment || !toValue(options.enabled)) return;

    const lazyLoadedSentry = await import("@sentry/nuxt");
    const sentryReplayIntegration = lazyLoadedSentry
      .getClient()
      ?.getIntegrationByName("Replay");

    // Check if the Replay integration is already added.
    if (sentryReplayIntegration) {
      replaysLoaded.value = true;

      return;
    }

    try {
      addIntegration(
        lazyLoadedSentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [
            /https:\/\/api.matchwornshirt.com\/.*/,
            /https:\/\/api.mws.com\/.*/,
          ],
          networkRequestHeaders: ["X-Custom-Header"],
          networkResponseHeaders: ["X-Custom-Header"],
        }),
      );
    } finally {
      replaysLoaded.value = true;
    }
  });

  watchEffect(() => {
    if (!shouldAutoStartReplay.value) return;

    if (options.name) {
      setTag("replayName", options.name);
    }

    const replay = getReplay();
    replay?.start();
  });

  onBeforeUnmount(() => {
    if (!shouldAutoStartReplay.value) return;

    const replay = getReplay();
    replay?.stop();
  });
};

export default useSentryReplays;
